import { toast } from 'sonner';
import i18next from 'i18next';
import { useInterestedPartyStore } from '../../store/interested-party.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
export class InterestedPartyIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchAll(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'desc', columnFilters = {}) {
        const store = useInterestedPartyStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        store.clearError();
        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }
        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                populate: 'evidenceDocumentId',
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) acc[`filter_${key}`] = value;
                    return acc;
                }, {})
            });
            const { data, pagination, uniqueValues } = response;
            store.setInterestedParties(data || []);
            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                });
            }
            if (uniqueValues) store.setUniqueValues(uniqueValues);
            return { success: true };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotLoadList', { defaultValue: 'Could not load list' });
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async create(data) {
        const store = useInterestedPartyStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('contextManagementInterestedParties:messages.createSuccess', { defaultValue: 'Record created' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotCreateRecord', { defaultValue: 'Could not create record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async update(id, data) {
        const store = useInterestedPartyStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('contextManagementInterestedParties:messages.updateSuccess', { defaultValue: 'Record updated' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotUpdateRecord', { defaultValue: 'Could not update record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async delete(id) {
        const store = useInterestedPartyStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.delete(id);
            toast.success(i18next.t('contextManagementInterestedParties:messages.deleteSuccess', { defaultValue: 'Record deleted' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotDeleteRecord', { defaultValue: 'Could not delete record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async bulkDelete(ids) {
        const store = useInterestedPartyStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        if (!tenantId) {
            store.setLoading(false);
            return {
                success: false,
                error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }),
            };
        }
        try {
            const response = await this.service.bulkDelete(ids);
            toast.success(
                i18next.t('contextManagementInterestedParties:messages.bulkDeleteSuccess', {
                    count: ids.length,
                    defaultValue: '{{count}} records deleted',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotDeleteRecord', { defaultValue: 'Could not delete record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }
}
